import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { api } from '~/lib/api-client';
import { useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';

export const Route = createFileRoute('/leaderboard')({
  head: () =>
    buildHead({
      title: 'Leaderboard',
      description:
        'Top producers by genre. ELO-tracked ranks, wins, and seasonal rankings. One leaderboard per genre - phonk competes against phonk.',
      path: '/leaderboard',
    }),
  component: Leaderboard,
});

type Row = {
  rank: number;
  userId: string;
  handle: string;
  avatarUrl: string | null;
  matchesPlayed: number;
  wins: number;
  points: number;
};

type SeasonRow = {
  rank: number;
  userId: string;
  handle: string;
  avatarUrl: string | null;
  plan: 'free' | 'paid';
  genreSlug: string;
  rating: number;
  wins: number;
  losses: number;
  rewardTier: 'gold' | 'silver' | 'bronze' | null;
  prizeEligible: boolean;
};

type SeasonLeaderboard = {
  season: { slug: string; startsAt: string; endsAt: string };
  items: SeasonRow[];
};

type Genre = { id: string; slug: string; name: string; kind: 'system' | 'user' };

const MODES = [
  { value: 'ranked', label: 'Ranked' },
  { value: 'quickplay', label: 'Quick Play' },
  { value: 'flip', label: 'Sample Flip' },
  { value: 'private', label: 'Private' },
  { value: '', label: 'All modes' },
] as const;

// Reward tier colored pill. Shows a text badge with a background tint.
function TierBadge({ tier }: { tier: SeasonRow['rewardTier'] }) {
  if (!tier) return null;
  const styles: Record<NonNullable<SeasonRow['rewardTier']>, string> = {
    gold: 'bg-yellow-400/20 text-yellow-700 dark:text-yellow-300 ring-yellow-400/40',
    silver: 'bg-slate-400/20 text-slate-600 dark:text-slate-300 ring-slate-400/40',
    bronze: 'bg-orange-400/20 text-orange-700 dark:text-orange-300 ring-orange-400/40',
  };
  const labels: Record<NonNullable<SeasonRow['rewardTier']>, string> = {
    gold: 'Gold',
    silver: 'Silver',
    bronze: 'Bronze',
  };
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${styles[tier]}`}
    >
      {labels[tier]}
    </span>
  );
}

function SeasonLeaderboardView({ slug }: { slug: string }) {
  const { data: session } = useSession();
  const [data, setData] = useState<SeasonLeaderboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api<SeasonLeaderboard>(`/leaderboard/season/${slug}`)
      .then(setData)
      .catch(() => setError('Season not found.'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <p className="text-sm text-muted-foreground">Loading season...</p>;
  if (error || !data) {
    return <p className="text-sm text-muted-foreground">{error ?? 'Failed to load.'}</p>;
  }

  return (
    <>
      <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
        Season <strong>{data.season.slug}</strong> - ends{' '}
        {new Date(data.season.endsAt).toLocaleDateString()}
      </div>

      {data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No rankings yet for this season.</p>
      ) : (
        <table className="w-full border-separate border-spacing-y-1 text-sm">
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th className="w-10 text-left">#</th>
              <th className="text-left">Producer</th>
              <th className="w-24 text-left">Tier</th>
              <th className="w-20 text-right">Rating</th>
              <th className="w-16 text-right">W</th>
              <th className="w-16 text-right">L</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => {
              const mine = r.userId === session?.id;
              return (
                <tr
                  key={`${r.userId}-${r.genreSlug}`}
                  className={
                    mine ? 'rounded-md bg-primary/10 ring-1 ring-primary' : 'border border-border'
                  }
                >
                  <td className="px-2 py-1.5 tabular-nums">{r.rank}</td>
                  <td className="px-2 py-1.5">
                    {r.avatarUrl && (
                      // biome-ignore lint/a11y/useAltText: decorative
                      <img src={r.avatarUrl} className="mr-2 inline-block h-5 w-5 rounded-full" />
                    )}
                    <Link
                      to="/$username"
                      params={{ username: r.handle }}
                      className="hover:text-primary hover:underline"
                    >
                      @{r.handle}
                    </Link>
                    {r.plan === 'paid' && (
                      <span className="ml-1.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary ring-1 ring-primary/30">
                        Pro
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <TierBadge tier={r.rewardTier} />
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{Math.round(r.rating)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{r.wins}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{r.losses}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

function Leaderboard() {
  const { data: session } = useSession();
  const [mode, setMode] = useState('ranked');
  const [genreSlug, setGenreSlug] = useState('');
  const [items, setItems] = useState<Row[]>([]);
  const [me, setMe] = useState<Row | null>(null);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [loading, setLoading] = useState(true);
  // Seasonal view: when set, shows the season-slug tab instead of the points board.
  const [seasonSlug, setSeasonSlug] = useState('');

  useEffect(() => {
    api<{ items: Genre[] }>('/genres').then((r) => setGenres(r.items));
  }, []);

  useEffect(() => {
    if (seasonSlug) return; // skip points-board fetch when viewing a season
    setLoading(true);
    const q = new URLSearchParams();
    if (mode) q.set('mode', mode);
    if (genreSlug) q.set('genreSlug', genreSlug);
    api<{ items: Row[]; me: Row | null }>(`/leaderboard?${q.toString()}`)
      .then((r) => {
        setItems(r.items);
        setMe(r.me);
      })
      .finally(() => setLoading(false));
  }, [mode, genreSlug, seasonSlug]);

  const myRank = me?.rank ?? null;

  return (
    <section className="mx-auto max-w-3xl px-4 py-10 space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-2xl font-bold">Leaderboard</h2>
        {!seasonSlug && (
          <div className="flex gap-2">
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            >
              {MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <select
              value={genreSlug}
              onChange={(e) => setGenreSlug(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            >
              <option value="">All genres</option>
              {genres.map((g) => (
                <option key={g.id} value={g.slug}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {/* Season slug input - allows jumping to a specific season */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Season slug (optional)"
            value={seasonSlug}
            onChange={(e) => setSeasonSlug(e.target.value.trim())}
            className="rounded-md border border-border bg-background px-2 py-1 text-sm w-40"
          />
          {seasonSlug && (
            <button
              type="button"
              onClick={() => setSeasonSlug('')}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          )}
        </div>
      </header>

      {seasonSlug ? (
        <SeasonLeaderboardView slug={seasonSlug} />
      ) : (
        <>
          {session && me && (
            <div className="rounded-md border border-primary bg-primary/10 p-3 text-sm">
              You're at <strong>#{myRank}</strong> - {me.wins} wins - {me.matchesPlayed} matches -{' '}
              <strong>{me.points} pts</strong>
            </div>
          )}
          {session && !me && !loading && (
            <div className="rounded-md border border-border p-3 text-sm text-muted-foreground">
              Play a match to get on the leaderboard.
            </div>
          )}
          {!session && (
            <div className="rounded-md border border-border p-3 text-sm text-muted-foreground">
              <a href="/auth/sign-in" className="underline">
                Sign in
              </a>{' '}
              to track your rank.
            </div>
          )}

          {loading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No ranked results yet. Be the first.</p>
          ) : (
            <table className="w-full border-separate border-spacing-y-1 text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="w-10 text-left">#</th>
                  <th className="text-left">Producer</th>
                  <th className="w-16 text-right">Wins</th>
                  <th className="w-20 text-right">Matches</th>
                  <th className="w-20 text-right">Points</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const mine = r.userId === session?.id;
                  return (
                    <tr
                      key={r.userId}
                      className={
                        mine
                          ? 'rounded-md bg-primary/10 ring-1 ring-primary'
                          : 'border border-border'
                      }
                    >
                      <td className="px-2 py-1.5 tabular-nums">{r.rank}</td>
                      <td className="px-2 py-1.5">
                        {r.avatarUrl && (
                          // biome-ignore lint/a11y/useAltText: decorative
                          <img
                            src={r.avatarUrl}
                            className="mr-2 inline-block h-5 w-5 rounded-full"
                          />
                        )}
                        <Link
                          to="/$username"
                          params={{ username: r.handle }}
                          className="hover:text-primary hover:underline"
                        >
                          @{r.handle}
                        </Link>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{r.wins}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{r.matchesPlayed}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold">
                        {r.points}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
